/**
 * Utility for handling scrollbar compensation to prevent UI shift when modals open
 * and body scroll is locked
 */

/**
 * Calculates the width of the scrollbar by creating a temporary element
 * @returns {number} The width of the scrollbar in pixels
 */
const getScrollbarWidth = (): number => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return 0;
    }

    // Create a temporary div element to measure scrollbar width
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // Force scrollbar
    (outer.style as CSSStyleDeclaration & { msOverflowStyle?: string }).msOverflowStyle = 'scrollbar'; // For IE and Edge
    document.body.appendChild(outer);

    // Create an inner element
    const inner = document.createElement('div');
    outer.appendChild(inner);

    // Calculate the difference between outer and inner width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    // Clean up
    outer.remove();

    return scrollbarWidth;
}

/**
 * Checks if the document body currently has a scrollbar
 * @returns {boolean} True if body has vertical scrollbar
 */
const hasVerticalScrollbar = (): boolean => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
    }

    return document.body.scrollHeight > window.innerHeight;
}

/**
 * Applies scrollbar compensation to prevent layout shift when locking body scroll
 * Should be called when opening a modal that locks body scroll
 */
const applyScrollbarCompensation = (): void => {
    // Check if compensation is already applied
    if (document.body.hasAttribute('data-original-padding-right')) {
        return; // Compensation already applied, don't apply again
    }

    if (!hasVerticalScrollbar()) {
        return; // No scrollbar, no compensation needed
    }

    const scrollbarWidth = getScrollbarWidth();

    if (scrollbarWidth > 0) {
        // Store original padding-right for restoration later
        const originalPaddingRight = document.body.style.paddingRight;
        document.body.setAttribute('data-original-padding-right', originalPaddingRight);

        // Apply compensation
        const currentPaddingRight = parseInt(getComputedStyle(document.body).paddingRight, 10) || 0;
        document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }
}

/**
 * Removes scrollbar compensation and restores original padding
 * Should be called when closing a modal that locked body scroll
 */
const removeScrollbarCompensation = (): void => {
    // Restore original padding-right
    const originalPaddingRight = document.body.getAttribute('data-original-padding-right');

    if (originalPaddingRight !== null) {
        document.body.style.paddingRight = originalPaddingRight;
        document.body.removeAttribute('data-original-padding-right');
    } else {
        // If no original padding was stored, remove the style
        document.body.style.removeProperty('padding-right');
    }
}

/**
 * Enhanced body scroll lock that includes scrollbar compensation
 * @param {string} lockClass - The CSS class to apply for scroll lock
 */
export const lockBodyScrollWithCompensation = (lockClass: string = 'body-scroll-lock'): void => {
    // Apply scrollbar compensation first
    applyScrollbarCompensation();

    // Then apply the scroll lock class
    document.body.classList.add(lockClass);
    document.documentElement.classList.add(lockClass);
}

/**
 * Removes body scroll lock and scrollbar compensation
 * @param {string} lockClass - The CSS class to remove for scroll lock
 */
export const unlockBodyScrollWithCompensation = (lockClass: string = 'body-scroll-lock'): void => {
    // Remove scroll lock class first
    document.body.classList.remove(lockClass);
    document.documentElement.classList.remove(lockClass);

    // Then remove scrollbar compensation
    removeScrollbarCompensation();
}
